import { ApiError, ApiErrorData } from '@afinius/types/Error';
import { message } from 'ant-design-vue';
import ky, { type AfterResponseHook, isHTTPError, Options } from 'ky';

import { generateUrl } from './base-url';
import { getBearerToken, getLandlordBearerToken } from './bearer-token';
import { createJsonApiClient } from './json-api-client';
import { forgetSessionOfOtherApi } from './session-api';
import { getTenantUuid } from './tenant-uuid';

export const afterResponseHooks: AfterResponseHook[] = [];

export type BeforeErrorHook = (error: { category: string; action: string; label?: string }) => void;
export const beforeErrorHooks: BeforeErrorHook[] = [];

export type ApiOptions = Options;

const applyAuthHeaders = ({ request }: { request: Request }) => {
  const isLandlord = request.url.includes('/landlord/');

  if (isLandlord) {
    request.headers.set('Authorization', getLandlordBearerToken());
  } else {
    request.headers.set('Authorization', getBearerToken());
    request.headers.set('X-Tenant-Uuid', getTenantUuid());
  }

  request.headers.set('X-Application', import.meta.env.VITE_API_APPLICATION_KEY);
};

forgetSessionOfOtherApi();

const api = ky.create({
  prefix: generateUrl(),
  retry: { limit: 1, methods: [], statusCodes: [], delay: () => 0 },
  hooks: {
    beforeRequest: [applyAuthHeaders],
    beforeRetry: [applyAuthHeaders],
    beforeError: [
      ({ error }) => {
        if (!isHTTPError(error)) {
          return error;
        }

        const response = typeof error.data === 'object' ? (error.data as ApiErrorData) : undefined;

        if (location.href.includes('/auth/')) {
          (error as ApiError).response.data = response;

          return error;
        }

        if (error.response.status === 403 && response) {
          message.error(response.message);

          beforeErrorHooks.forEach((hook) =>
            hook({
              category: 'API',
              action: 'response_error',
              label: response.message,
            }),
          );
        }

        (error as ApiError).response.data = response;

        return error;
      },
    ],
    afterResponse: [
      async (state) => {
        for (const hook of afterResponseHooks) {
          const result = await hook(state);
          if (result !== undefined && result !== state.response) {
            return result;
          }
        }
      },
    ],
  },
});

const jsonApi = createJsonApiClient(api);
const retryRequest = ky.retry;

export { api, isHTTPError, jsonApi, retryRequest };
